/**
 * Group audit-log entries by local calendar day, preserving the
 * incoming order (newest-first) both at the group level and within
 * each group. Used by the timeline rail to render GitHub-style
 * date headers with the entries listed underneath.
 *
 * Mirrors `features/workflow-versions/utils/groupByDate.ts` — same
 * label semantics ("Today" / "Yesterday" / long-date for older).
 */

const LONG_DATE = new Intl.DateTimeFormat(undefined, {
  year: 'numeric',
  month: 'long',
  day: 'numeric',
});

function isoLocalDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function startOfDay(d) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function labelFor(date, now) {
  const dayMs = 24 * 60 * 60 * 1000;
  const diffDays = Math.round(
    (startOfDay(now).getTime() - startOfDay(date).getTime()) / dayMs,
  );
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  return LONG_DATE.format(date);
}

/**
 * @param {Array<{ changed_at: string }>} entries - newest-first
 * @param {Date} [now]
 * @returns {Array<{ isoDate: string, label: string, entries: any[] }>}
 */
export function groupAuditEntriesByDate(entries, now = new Date()) {
  const groups = [];
  let current = null;
  for (const entry of entries) {
    const created = new Date(entry.changed_at);
    const iso = isoLocalDate(created);
    if (!current || current.isoDate !== iso) {
      current = {
        isoDate: iso,
        label: labelFor(created, now),
        entries: [],
      };
      groups.push(current);
    }
    current.entries.push(entry);
  }
  return groups;
}
